import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { US_STATE_OPTIONS, normalizeUsState } from "../../../utils/usStates";

const API_BASE_URL = "https://api.home-helper.com";

const INITIAL_FORM_STATE = {
  name: "",
  phone: "",
  email: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  notes: "",
};

function normalizePhoneForPayload(value) {
  const digits = String(value || "").replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }

  return value.trim();
}

function validateForm(values) {
  const requiredFields = ["name", "email", "street", "city", "state", "zip"];

  for (const field of requiredFields) {
    if (!String(values[field] || "").trim()) {
      return "Please complete all required fields.";
    }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(String(values.email || "").trim())) {
    return "Please enter a valid email address.";
  }

  const phoneDigits = String(values.phone || "").replace(/\D/g, "");
  if (phoneDigits && !(phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith("1")))) {
    return "Please enter a valid phone number or leave it blank.";
  }

  const zipRegex = /^\d{5}$/;
  if (!zipRegex.test(String(values.zip || "").trim())) {
    return "Please enter a valid 5-digit ZIP code.";
  }

  if (!normalizeUsState(values.state)) {
    return "Please select a valid state.";
  }

  return "";
}

function buildInitialState(initialZipCode) {
  return {
    ...INITIAL_FORM_STATE,
    zip: String(initialZipCode || "").trim(),
  };
}

export default function ProjectDiagnosticLeadForm({
  ctaLabel,
  formTitle,
  formDescription,
  successMessage,
  diagnosticContext,
  initialZipCode,
}) {
  const [formValues, setFormValues] = useState(() => buildInitialState(initialZipCode));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const normalizedInitialZip = useMemo(
    () => String(initialZipCode || "").trim(),
    [initialZipCode]
  );

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const nextValues = {
      ...formValues,
      state: normalizeUsState(formValues.state) || "",
      zip: String(formValues.zip || "").trim(),
    };
    const validationError = validateForm(nextValues);

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tools/project-diagnostic/lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: nextValues.name.trim(),
          phone: normalizePhoneForPayload(nextValues.phone),
          email: nextValues.email.trim(),
          street: nextValues.street.trim(),
          city: nextValues.city.trim(),
          state: nextValues.state,
          zip: nextValues.zip,
          notes: nextValues.notes.trim(),
          categoryKey:
            diagnosticContext.normalizedCategoryKey ||
            diagnosticContext.categoryKey ||
            "",
          diagnosticTitle: diagnosticContext.diagnosticTitle,
          diagnosticDescription: diagnosticContext.diagnosticDescription,
          summary: diagnosticContext.summary,
          severity: diagnosticContext.severity,
          severityLabel: diagnosticContext.severityLabel,
          answers: diagnosticContext.answers,
          categoryLabel: diagnosticContext.categoryLabel,
          categoryType: diagnosticContext.categoryType,
          pageUrl: diagnosticContext.pageUrl,
          submittedFrom: "project_diagnostic_result",
          submittedAt: new Date().toISOString(),
          zipCodeFromUrl: normalizedInitialZip || null,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Unable to save your project request.");
      }

      setSuccess(data);
    } catch (submitError) {
      console.error("Project diagnostic lead submit failed", submitError);
      setError(
        submitError.message ||
          "We couldn’t save your request right now. Please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        style={{
          marginTop: 18,
          padding: "18px 20px",
          borderRadius: 16,
          border: "1px solid #D1FAE5",
          backgroundColor: "#ECFDF5",
        }}
      >
        <strong style={{ display: "block", marginBottom: 6 }}>{successMessage}</strong>
        <p className="tool-muted" style={{ margin: 0 }}>
          Your project details were saved and can now be reviewed by local pros.
        </p>
      </div>
    );
  }

  return (
    <section
      style={{
        marginTop: 20,
        padding: "20px 22px",
        borderRadius: 18,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#F8FAFC",
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <strong style={{ display: "block", marginBottom: 6 }}>{formTitle}</strong>
        <p className="tool-muted" style={{ margin: 0 }}>
          {formDescription}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="tool-label">Name</span>
            <input
              type="text"
              value={formValues.name}
              onChange={(event) => handleChange("name", event.target.value)}
              disabled={loading}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="tool-label">Phone</span>
            <input
              type="tel"
              value={formValues.phone}
              onChange={(event) => handleChange("phone", event.target.value)}
              disabled={loading}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="tool-label">Email</span>
            <input
              type="email"
              value={formValues.email}
              onChange={(event) => handleChange("email", event.target.value)}
              disabled={loading}
            />
          </label>

          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              gridColumn: "1 / -1",
            }}
          >
            <span className="tool-label">Street Address</span>
            <input
              type="text"
              value={formValues.street}
              onChange={(event) => handleChange("street", event.target.value)}
              disabled={loading}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="tool-label">City</span>
            <input
              type="text"
              value={formValues.city}
              onChange={(event) => handleChange("city", event.target.value)}
              disabled={loading}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="tool-label">State</span>
            <select
              value={normalizeUsState(formValues.state) || ""}
              onChange={(event) => handleChange("state", event.target.value)}
              disabled={loading}
            >
              <option value="">Select State</option>
              {US_STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="tool-label">ZIP Code</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={formValues.zip}
              onChange={(event) => handleChange("zip", event.target.value)}
              disabled={loading}
            />
          </label>

          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              gridColumn: "1 / -1",
            }}
          >
            <span className="tool-label">Notes or Contact Preferences (Optional)</span>
            <textarea
              rows={4}
              value={formValues.notes}
              onChange={(event) => handleChange("notes", event.target.value)}
              disabled={loading}
            />
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <button type="submit" className="tool-button-primary" disabled={loading}>
            {loading ? "Sending your project..." : ctaLabel}
          </button>
        </div>

        {error ? (
          <p className="tool-muted" style={{ marginTop: 10, color: "#B91C1C" }}>
            {error}
          </p>
        ) : null}
      </form>
    </section>
  );
}

ProjectDiagnosticLeadForm.propTypes = {
  ctaLabel: PropTypes.string.isRequired,
  formTitle: PropTypes.string.isRequired,
  formDescription: PropTypes.string.isRequired,
  successMessage: PropTypes.string.isRequired,
  initialZipCode: PropTypes.string,
  diagnosticContext: PropTypes.shape({
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        question: PropTypes.string,
        answer: PropTypes.string,
        answerLabel: PropTypes.string,
        answerValue: PropTypes.string,
      })
    ).isRequired,
    categoryKey: PropTypes.string,
    normalizedCategoryKey: PropTypes.string,
    categoryLabel: PropTypes.string,
    categoryType: PropTypes.string,
    diagnosticTitle: PropTypes.string,
    diagnosticDescription: PropTypes.string,
    pageUrl: PropTypes.string,
    severity: PropTypes.string,
    severityLabel: PropTypes.string,
    summary: PropTypes.string,
  }).isRequired,
};
